/**
 * ANINA · Compare com um seu (blocks/anina-comparar.liquid)
 * Busca qualquer perfume na base própria (/apps/circulo/perfumes) e compara com o produto da página:
 * afinidade, notas em comum, famílias e pirâmides. Sem login; com login guarda no perfumário.
 * Produto ligado à base (anina.base_id) usa a ficha da base; sem ligação, as notas do produto
 * passam pelo dicionário (assets/anina-notas-dic.js).
 * Abaixo da comparação, "Mais parecidos com o seu": perfumes da loja (/collections/all?view=guia-dados)
 * com maior afinidade com o perfume do cliente; cada card abre a comparação daquele produto.
 */
(() => {
  if (customElements.get('anina-comparar')) return;

  const API = '/apps/circulo/perfumes';
  const IMG = 'https://perfumes-anina.aninaparfums.workers.dev';
  const ORDEM = ['amadeirado', 'floral', 'citrico', 'doce', 'ambar', 'frutado', 'almiscarado'];
  const FAM = {
    amadeirado: ['Amadeirado', 'amadeirado', 'amadeirados'],
    floral: ['Floral', 'floral', 'florais'],
    citrico: ['Cítrico e fresco', 'fresco', 'frescos'],
    doce: ['Doce e gourmand', 'doce', 'doces'],
    ambar: ['Âmbar e especiarias', 'ambarado', 'ambarados'],
    frutado: ['Frutado', 'frutado', 'frutados'],
    almiscarado: ['Almiscarado e couro', 'almiscarado', 'almiscarados'],
  };
  const ESTAGIOS = ['Topo', 'Coração', 'Fundo', 'Outras notas'];
  // Cor do ponto de cada família (legenda e notas)
  const COR = {
    amadeirado: '#7a4e2a',
    floral: '#c2577e',
    citrico: '#5f9440',
    doce: '#c99a5b',
    ambar: '#b4451f',
    frutado: '#e08a2e',
    almiscarado: '#8f8579',
  };

  const esc = (s) =>
    String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const juntar = (l) => (l.length < 2 ? l.join('') : `${l.slice(0, -1).join(', ')} e ${l.at(-1)}`);
  const imagem = (id) => `${IMG}/${String(id).padStart(4, '0')}.webp`;
  const faixa = (n) => (n >= 75 ? 'Muito parecidos' : n >= 55 ? 'Parecidos' : n >= 35 ? 'Têm pontos em comum' : 'Bem diferentes');
  const minus = (s) => String(s).charAt(0).toLowerCase() + String(s).slice(1);
  const ponto = (f) => (f ? `<i class="anina-cmp__ponto" style="--cor:${COR[f]}" aria-hidden="true"></i>` : '');
  // Família principal de uma nota, pelo dicionário
  const familiaDe = (nome) => {
    const w = window.aninaNotas?.canonico(nome)?.w;
    if (!w) return null;
    let melhor = null;
    for (const f of ORDEM) if ((w[f] || 0) > (melhor ? w[melhor] : 0)) melhor = f;
    return melhor;
  };
  const reais = (c) => (c / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const caminho = (u) => {
    try {
      return new URL(u, location.origin).pathname.replace(/\/$/, '');
    } catch {
      return '';
    }
  };
  const PARECIDOS = 4;
  const PISO = 40;
  let catalogo = null;

  // Perfumes da loja com notas, preço e id na base (uma vez por página)
  function carregarCatalogo() {
    if (catalogo) return catalogo;
    const raiz = window.Shopify?.routes?.root || '/';
    catalogo = (async () => {
      const itens = [];
      for (let pg = 1; pg <= 6; pg++) {
        const t = await fetch(`${raiz}collections/all?view=guia-dados&page=${pg}`).then((r) => (r.ok ? r.text() : ''));
        const i = t.indexOf('ANINA_DADOS_INICIO');
        const f = t.indexOf('ANINA_DADOS_FIM');
        if (i < 0 || f < 0) break;
        const d = JSON.parse(t.slice(i + 'ANINA_DADOS_INICIO'.length, f));
        itens.push(...d.itens);
        if (d.pagina >= d.paginas) break;
      }
      return itens;
    })().catch(() => []);
    return catalogo;
  }

  class AninaComparar extends HTMLElement {
    connectedCallback() {
      try {
        this.dados = JSON.parse(this.querySelector('[data-cmp-dados]').textContent);
      } catch {
        return;
      }
      this.dados.nt = (this.dados.nt || []).map((x) =>
        String(x || '')
          .split(';')
          .map((n) => n.trim())
          .filter(Boolean)
      );
      this.dialog = this.querySelector('dialog');
      this.campo = this.querySelector('[data-cmp-campo]');
      this.lista = this.querySelector('[data-cmp-lista]');
      this.ctrl = new AbortController();
      const s = { signal: this.ctrl.signal };

      this.querySelector('[data-cmp-abrir]')?.addEventListener('click', () => this.abrir(), s);
      this.dialog.addEventListener('click', (e) => this.clique(e), s);
      this.dialog.addEventListener('close', () => document.documentElement.classList.remove('anina-cmp-aberto'), s);
      this.campo.addEventListener(
        'input',
        () => {
          clearTimeout(this.timer);
          this.timer = setTimeout(() => this.buscar(), 200);
        },
        s
      );
      this.campo.addEventListener('keydown', (e) => this.teclas(e), s);

      const pedido = new URLSearchParams(location.search).get('comparar');
      if (pedido && /^\d{1,6}$/.test(pedido)) {
        this.abrir();
        this.comparar(Number(pedido));
      }
    }

    disconnectedCallback() {
      this.ctrl?.abort();
      clearTimeout(this.timer);
    }

    abrir() {
      if (!this.dialog.open) this.dialog.showModal();
      document.documentElement.classList.add('anina-cmp-aberto');
      this.mostrar('busca');
      requestAnimationFrame(() => this.campo.focus());
    }

    mostrar(tela) {
      this.dialog.dataset.tela = tela;
      this.querySelector('[data-cmp-busca]').hidden = tela !== 'busca';
      this.querySelector('[data-cmp-resultado]').hidden = tela === 'busca';
    }

    clique(e) {
      if (e.target === this.dialog || e.target.closest('[data-cmp-fechar]')) return this.dialog.close();
      if (e.target.closest('[data-cmp-trocar]')) {
        this.mostrar('busca');
        this.campo.select();
        return this.campo.focus();
      }
      const op = e.target.closest('[data-cmp-id]');
      if (op) return this.comparar(Number(op.dataset.cmpId));
      if (e.target.closest('[data-cmp-sacola]')) return this.sacola();
      if (e.target.closest('[data-cmp-guardar]')) return this.guardar(e.target.closest('[data-cmp-guardar]'));
    }

    teclas(e) {
      const ops = [...this.lista.querySelectorAll('[data-cmp-id]')];
      if (!ops.length) return;
      let i = ops.findIndex((x) => x.getAttribute('aria-selected') === 'true');
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        i = e.key === 'ArrowDown' ? (i + 1) % ops.length : (i - 1 + ops.length) % ops.length;
        ops.forEach((x, j) => x.setAttribute('aria-selected', String(j === i)));
        ops[i].scrollIntoView({ block: 'nearest' });
        this.campo.setAttribute('aria-activedescendant', ops[i].id);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        (ops[i] || ops[0]).click();
      }
    }

    async buscar() {
      const q = this.campo.value.trim();
      const aviso = this.querySelector('[data-cmp-aviso]');
      if (q.length < 2) {
        this.lista.innerHTML = '';
        aviso.hidden = true;
        return;
      }
      this.buscaCtrl?.abort();
      this.buscaCtrl = new AbortController();
      let res = [];
      try {
        const r = await fetch(`${API}/busca?q=${encodeURIComponent(q)}`, { signal: this.buscaCtrl.signal });
        if (!r.ok) throw new Error();
        res = (await r.json()).resultados || [];
      } catch (err) {
        if (err.name === 'AbortError') return;
        this.lista.innerHTML = '';
        aviso.hidden = false;
        aviso.querySelector('[data-cmp-aviso-texto]').textContent = 'Não conseguimos buscar agora. Tente de novo em instantes.';
        return;
      }
      this.lista.innerHTML = res
        .map(
          (p, i) => `<li><button type="button" role="option" id="cmp-op-${i}" aria-selected="false" data-cmp-id="${p.id}">
            <span class="anina-cmp__mini"><img src="${esc(p.img)}" alt="" width="48" height="64" loading="lazy"></span>
            <span class="anina-cmp__op-txt"><small>${esc(p.m)}</small><b>${esc(p.n)}</b><small>${esc([p.c, p.a].filter(Boolean).join(' · '))}</small></span>
          </button></li>`
        )
        .join('');
      aviso.hidden = res.length > 0;
      if (!res.length) aviso.querySelector('[data-cmp-aviso-texto]').textContent = `Ainda não temos “${q}” na base.`;
    }

    async comparar(id) {
      this.mostrar('resultado');
      const box = this.querySelector('[data-cmp-resultado-corpo]');
      box.innerHTML = '<p class="anina-cmp__carregando">Comparando…</p>';
      const d = this.dados;
      try {
        const ids = [id, d.bi].filter(Boolean).join(',');
        const r = await fetch(`${API}/ficha?id=${ids}`);
        if (!r.ok) throw new Error();
        const fichas = (await r.json()).fichas || [];
        const outra = fichas.find((f) => f.id === id);
        if (!outra) throw new Error();
        let nossa = d.bi && fichas.find((f) => f.id === d.bi);
        if (!nossa) {
          if (!window.aninaNotas) throw new Error();
          nossa = { nt: d.nt, ...window.aninaNotas.perfil(d.nt) };
        }
        this.outra = outra;
        this.pedido = id;
        box.innerHTML = this.html(nossa, outra);
        const pir = box.querySelector('[data-cmp-pir]');
        if (pir && window.matchMedia('(min-width: 990px)').matches) pir.open = true;
        this.parecidos(outra, id);
        this.dialog.querySelector('.anina-cmp__painel')?.scrollTo(0, 0);
        this.querySelector('[data-cmp-titulo-res]')?.focus();
      } catch {
        box.innerHTML = `<p class="anina-cmp__carregando">Não conseguimos carregar a comparação agora. Tente de novo em instantes.</p>`;
      }
    }

    html(a, b) {
      const d = this.dados;
      const mesmo = d.bi && d.bi === b.id;
      const ka = new Set([].concat(...(a.k || [])));
      const kb = new Set([].concat(...(b.k || [])));
      const comunsK = [...ka].filter((k) => kb.has(k));
      const nomeDe = (p, k) => {
        for (let i = 0; i < (p.k || []).length; i++) {
          const j = p.k[i].indexOf(k);
          if (j >= 0) return p.nt[i][j];
        }
        return k;
      };
      const comuns = comunsK.map((k) => nomeDe(a, k));
      const nota = mesmo ? 100 : window.aninaNotas ? window.aninaNotas.afinidade(a, b).nota : this.afinidade(a, b, comunsK.length);
      const na = d.n;
      const nb = b.n === na ? `seu ${b.n}` : b.n;

      const ga = a.gr || {};
      const gb = b.gr || {};
      const fams = ORDEM.filter((f) => (ga[f] || 0) + (gb[f] || 0) >= 1)
        .sort((x, y) => (gb[y] || 0) + (ga[y] || 0) - (gb[x] || 0) - (ga[x] || 0))
        .slice(0, 6);
      const escala = Math.max(40, ...fams.map((f) => Math.max(ga[f] || 0, gb[f] || 0)));
      const barra = (v, cls) => `<span class="anina-cmp__trilho"><span class="${cls}" style="width:${Math.round(((v || 0) / escala) * 100)}%"></span></span>`;

      // Notas de cada perfume, sem repetir, com a família
      const notasDe = (p) => {
        const vistos = new Set();
        const out = [];
        (p.nt || []).forEach((l, i) =>
          (l || []).forEach((n, j) => {
            const k = p.k?.[i]?.[j] ?? n;
            if (vistos.has(k)) return;
            vistos.add(k);
            out.push({ n, k, f: familiaDe(n) });
          })
        );
        return out;
      };
      const la = notasDe(a);
      const lb = notasDe(b);
      const iguais = la.filter((x) => kb.has(x.k));
      const soA0 = la.filter((x) => !kb.has(x.k));
      const soB0 = lb.filter((x) => !ka.has(x.k));
      const famA = new Set(soA0.map((x) => x.f).filter(Boolean));
      const famB = new Set(soB0.map((x) => x.f).filter(Boolean));
      const parecidas = ORDEM.filter((f) => famA.has(f) && famB.has(f)).map((f) => ({ f, a: soA0.filter((x) => x.f === f), b: soB0.filter((x) => x.f === f) }));
      const emPar = new Set(parecidas.map((x) => x.f));
      const soA = soA0.filter((x) => !emPar.has(x.f));
      const soB = soB0.filter((x) => !emPar.has(x.f));
      const presentes = ORDEM.filter((f) => la.some((x) => x.f === f) || lb.some((x) => x.f === f));
      const chip = (x) => `<li>${ponto(x.f)}${esc(x.n)}</li>`;
      const nomes = (l) => l.map((x) => esc(x.n)).join(', ');
      const grupoSo = (titulo, l) =>
        `<div class="anina-cmp__grupo"><p class="anina-cmp__rot">${esc(titulo)} · ${l.length}</p><ul class="anina-cmp__chips is-leve" role="list">${l.map(chip).join('')}</ul></div>`;
      const notas = `<section class="anina-cmp__bloco anina-cmp__notas" aria-label="Notas">
          <h3 class="anina-cmp__rot">Notas</h3>
          ${presentes.length ? `<ul class="anina-cmp__legenda-fam" role="list" aria-label="Cor de cada família">${presentes.map((f) => `<li>${ponto(f)}${FAM[f][0]}</li>`).join('')}</ul>` : ''}
          <div class="anina-cmp__grupos">
            ${iguais.length ? `<div class="anina-cmp__grupo is-iguais"><p class="anina-cmp__rot">Nos dois · ${iguais.length}</p><ul class="anina-cmp__chips" role="list">${iguais.slice(0, 12).map(chip).join('')}</ul></div>` : ''}
            ${
              parecidas.length
                ? `<div class="anina-cmp__grupo is-parecidas"><p class="anina-cmp__rot">Parecidas · ${parecidas.length} ${parecidas.length > 1 ? 'famílias' : 'família'}</p>
              <p class="anina-cmp__grupo-sub">Notas diferentes, da mesma família nos dois.</p>
              ${parecidas
                .map(
                  (x) => `<div class="anina-cmp__par"><p class="anina-cmp__par-fam">${ponto(x.f)}${FAM[x.f][0]}</p>
                <p><small>${esc(na)}</small>${nomes(x.a)}</p><p><small>${esc(b.n)}</small>${nomes(x.b)}</p></div>`
                )
                .join('')}</div>`
                : ''
            }
            ${soA.length ? grupoSo(`Só no ${na}`, soA) : ''}
            ${soB.length ? grupoSo(`Só no ${b.n}`, soB) : ''}
          </div>
        </section>`;

      // Pirâmide espelhada: as notas dos dois vêm primeiro em cada etapa, na mesma ordem
      const ordemA = la.map((x) => x.k);
      const etapa = (p, i) =>
        (p.nt?.[i] || [])
          .map((n, j) => {
            const k = p.k?.[i]?.[j] ?? n;
            return { n, k, f: familiaDe(n), c: ka.has(k) && kb.has(k) };
          })
          .sort((x, y) => y.c - x.c || (x.c && y.c ? ordemA.indexOf(x.k) - ordemA.indexOf(y.k) : 0))
          .map((x) => `<li${x.c ? ' class="is-comum"' : ''}>${ponto(x.f)}<span>${esc(x.n)}</span></li>`)
          .join('') || '<li class="is-vazio">—</li>';
      const estagios = [0, 1, 2, 3]
        .filter((i) => (a.nt?.[i] || []).length || (b.nt?.[i] || []).length)
        .map((i) => `<div class="anina-cmp__estagio"><ul class="is-esq" role="list">${etapa(a, i)}</ul><p class="anina-cmp__rot">${ESTAGIOS[i]}</p><ul class="is-dir" role="list">${etapa(b, i)}</ul></div>`)
        .join('');

      const fotoNossa = d.bi ? imagem(d.bi) : d.img;
      return `<div class="anina-cmp__col">
        <h2 class="anina-cmp__titulo" tabindex="-1" data-cmp-titulo-res>${esc(na)} <span>x</span> ${esc(b.n)}</h2>
        <div class="anina-cmp__frascos">
          <figure class="anina-cmp__frasco is-nosso">
            <div class="anina-cmp__foto${d.bi ? '' : ' is-loja'}">${fotoNossa ? `<img src="${esc(fotoNossa)}" alt="Frasco do ${esc(na)}" width="375" height="500">` : ''}</div>
            <figcaption><span class="anina-cmp__rot is-ouro">Na Anina</span><b>${esc(na)}</b><small>${esc([d.m, d.c].filter(Boolean).join(' · '))}</small></figcaption>
          </figure>
          <figure class="anina-cmp__frasco">
            <div class="anina-cmp__foto"><img src="${esc(imagem(b.id))}" alt="Frasco do ${esc(b.n)}" width="375" height="500"></div>
            <figcaption><span class="anina-cmp__rot">O seu</span><b>${esc(b.n)}</b><small>${esc([b.m, b.c].filter(Boolean).join(' · '))}</small>
            <button type="button" class="anina-cmp__link" data-cmp-trocar>Trocar perfume</button></figcaption>
          </figure>
        </div>
        <section class="anina-cmp__afin" aria-label="Afinidade">
          <p class="anina-cmp__afin-topo"><span class="anina-cmp__rot">Afinidade</span><span class="anina-cmp__faixa">${mesmo ? 'O mesmo perfume' : faixa(nota)}</span></p>
          <p class="anina-cmp__num">${nota}%</p>
          <span class="anina-cmp__trilho is-grosso" role="img" aria-label="${nota} por cento de afinidade"><span class="is-a" style="width:${nota}%"></span></span>
          <p class="anina-cmp__frase">${esc(mesmo ? `Você já conhece o ${na}: é este mesmo perfume.` : this.frase(ga, gb, comuns, na, nb))}</p>
        </section>
        <div class="anina-cmp__acoes">
          ${d.vende ? `<button type="button" class="button anina-cmp__btn" data-cmp-sacola>Adicionar o ${esc(na)} à sacola</button>` : ''}
          ${
            d.logado
              ? `<button type="button" class="button-secondary anina-cmp__btn" data-cmp-guardar>Guardar o ${esc(b.n)} no meu perfumário</button>`
              : `<a class="button-secondary anina-cmp__btn" href="${esc(d.login)}?return_url=${encodeURIComponent(`${location.pathname}?comparar=${b.id}`)}&amp;return_to=${encodeURIComponent(`${location.pathname}?comparar=${b.id}`)}">Entrar para guardar no perfumário</a>`
          }
        </div>
        </div><div class="anina-cmp__col">
        ${notas}
        ${fams.length ? `<section class="anina-cmp__bloco"><div class="anina-cmp__bloco-topo"><h3 class="anina-cmp__rot">Famílias</h3>
          <p class="anina-cmp__legenda"><span><i class="is-a"></i>${esc(na)}</span><span><i class="is-b"></i>${esc(b.n)}</span></p></div>
          <ul class="anina-cmp__fams" role="list">${fams
            .map(
              (f) => `<li><p><span>${FAM[f][0]}</span><span>${Math.round(ga[f] || 0)}% · ${Math.round(gb[f] || 0)}%</span></p>${barra(ga[f], 'is-a')}${barra(gb[f], 'is-b')}</li>`
            )
            .join('')}</ul></section>` : ''}
        ${
          estagios
            ? `<details class="anina-cmp__bloco anina-cmp__pir" data-cmp-pir>
          <summary class="anina-cmp__pir-botao"><span>Ver pirâmides completas</span><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></summary>
          <div class="anina-cmp__pir-corpo">
            <div class="anina-cmp__estagio anina-cmp__cab"><span class="is-esq">${esc(na)}</span><h3 class="anina-cmp__rot">Pirâmides</h3><span class="is-dir">${esc(b.n)}</span></div>
            ${estagios}
          </div>
        </details>
        <p class="anina-cmp__nota">${comuns.length ? 'Em dourado, as notas que aparecem nos dois. ' : ''}A comparação usa as notas publicadas pelas marcas; o cheiro na pele pode variar.</p>`
            : ''
        }
        </div>
        <section class="anina-cmp__parecidos" data-cmp-parecidos aria-busy="true" aria-label="Mais parecidos com o seu ${esc(b.n)}">
          <div class="anina-cmp__par-topo"><p class="anina-cmp__rot is-ouro">Na Anina</p>
            <h3 class="anina-cmp__par-titulo">Mais parecidos com o seu ${esc(b.n)}</h3>
            <p class="anina-cmp__par-sub">Pelas notas e famílias. Toque em um para ver a comparação completa.</p></div>
          <ul class="anina-cmp__cards is-carregando" role="list" data-cmp-cards>${'<li aria-hidden="true"><span class="anina-cmp__card-foto"></span><span class="anina-cmp__esq"></span><span class="anina-cmp__esq is-curta"></span></li>'.repeat(PARECIDOS)}</ul>
        </section>`;
    }

    async parecidos(b, pedido) {
      const itens = await carregarCatalogo();
      if (pedido !== this.pedido) return;
      const sec = this.querySelector('[data-cmp-parecidos]');
      if (!sec) return;
      const d = this.dados;
      const aqui = caminho(d.u || location.pathname);
      const vistos = new Set();
      const lista = [];
      for (const p of itens) {
        if (!p.av || !p.pr || /\btester\b/i.test(p.t) || caminho(p.u) === aqui || (d.bi && p.bi === d.bi)) continue;
        const chave = p.bi || `${p.b}|${String(p.t).split(' · ')[0]}`.toLowerCase();
        if (vistos.has(chave)) continue;
        let nota;
        if (p.bi && p.bi === b.id) nota = 101;
        else {
          if (!window.aninaNotas) continue;
          if (p.pf === undefined) {
            const nt = String(p.nt || '')
              .split('|')
              .map((e) =>
                e
                  .split(';')
                  .map((n) => n.trim())
                  .filter(Boolean)
              );
            p.pf = nt.some((x) => x.length) ? window.aninaNotas.perfil(nt) : null;
          }
          if (!p.pf) continue;
          nota = window.aninaNotas.afinidade(p.pf, b).nota;
        }
        if (nota < PISO) continue;
        vistos.add(chave);
        lista.push({ p, nota });
      }
      const top = lista.sort((x, y) => y.nota - x.nota).slice(0, PARECIDOS);
      if (!top.length) return sec.remove();
      const ul = sec.querySelector('[data-cmp-cards]');
      ul.classList.remove('is-carregando');
      ul.innerHTML = top
        .map(({ p, nota }) => {
          const proprio = nota > 100;
          const nome = String(p.t).split(' · ')[0];
          return `<li><a class="anina-cmp__card" href="${esc(p.u)}?comparar=${b.id}">
            <span class="anina-cmp__card-foto">${p.img ? `<img src="${esc(p.img)}" alt="" width="480" height="600" loading="lazy">` : ''}
              <span class="anina-cmp__selo">${proprio ? 'É o seu' : `${nota}%`}</span></span>
            <small>${esc(p.b)}</small><b>${esc(nome)}</b>${p.k ? `<span class="anina-cmp__card-conc">${esc(p.k)}</span>` : ''}
            <span class="anina-cmp__card-faixa">${proprio ? 'O mesmo perfume, aqui na Anina' : faixa(nota)}</span>
            <span class="anina-cmp__card-preco">${p.pv ? 'A partir de ' : ''}${reais(p.pr)}</span>
          </a></li>`;
        })
        .join('');
      sec.removeAttribute('aria-busy');
    }

    afinidade(a, b, comuns) {
      const ga = a.gr || {};
      const gb = b.gr || {};
      const fam = ORDEM.reduce((s, f) => s + Math.min(ga[f] || 0, gb[f] || 0), 0) / 100;
      const na = new Set([].concat(...(a.k || []))).size;
      const nb = new Set([].concat(...(b.k || []))).size;
      const ns = na && nb ? (2 * comuns) / (na + nb) : 0;
      return Math.round(100 * (0.65 * fam + 0.35 * Math.min(1, ns * 1.6)));
    }

    frase(ga, gb, comuns, na, nb) {
      const juntos = ORDEM.filter((f) => Math.min(ga[f] || 0, gb[f] || 0) >= 15)
        .sort((x, y) => Math.min(ga[y] || 0, gb[y] || 0) - Math.min(ga[x] || 0, gb[x] || 0))
        .slice(0, 2);
      const notas = comuns.slice(0, 3).map(minus);
      let t = juntos.length ? `Os dois são ${juntar(juntos.map((f) => FAM[f][2]))}` : 'Os dois seguem caminhos diferentes';
      if (notas.length) t += `${juntos.length ? ', com' : ', mas dividem'} ${juntar(notas)}`;
      t += '.';
      const dif = (x, y) =>
        ORDEM.filter((f) => (x[f] || 0) - (y[f] || 0) >= 8)
          .sort((f, g) => (x[g] || 0) - (y[g] || 0) - ((x[f] || 0) - (y[f] || 0)))
          .slice(0, 2)
          .map((f) => FAM[f][1]);
      const ma = dif(ga, gb);
      const mb = dif(gb, ga);
      if (ma.length && mb.length) t += ` O ${na} é mais ${juntar(ma)}; o ${nb} é mais ${juntar(mb)}.`;
      else if (ma.length) t += ` O ${na} é mais ${juntar(ma)}.`;
      else if (mb.length) t += ` O ${nb} é mais ${juntar(mb)}.`;
      return t;
    }

    sacola() {
      const raiz = this.closest('product-information, .product-information, main') || document;
      const btn = raiz.querySelector('form[action*="/cart/add"] [type="submit"]') || document.querySelector('form[action*="/cart/add"] [type="submit"]');
      this.dialog.close();
      if (btn && !btn.disabled) btn.click();
      else btn?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }

    async guardar(botao) {
      const b = this.outra;
      if (!b) return;
      const perf = this.dados.perfumario && typeof this.dados.perfumario === 'object' ? this.dados.perfumario : {};
      const novo = {};
      for (const l of ['tenho', 'quero', 'nao']) novo[l] = (Array.isArray(perf[l]) ? perf[l] : []).filter((x) => x.p !== b.id);
      novo.tenho.unshift({ t: b.n, b: b.m, p: b.id });
      botao.disabled = true;
      try {
        const r = await fetch('/apps/circulo/conta', {
          method: 'POST',
          headers: { 'content-type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ perfumario: novo }),
        });
        if (!r.ok) throw new Error();
        this.dados.perfumario = (await r.json()).perfumario || novo;
        botao.outerHTML = `<a class="button-secondary anina-cmp__btn" href="${esc(this.dados.conta)}#conta-perfumario">Guardado · ver meu perfumário</a>`;
      } catch {
        botao.disabled = false;
        botao.textContent = 'Não deu para guardar. Tentar de novo';
      }
    }
  }

  customElements.define('anina-comparar', AninaComparar);
})();
